// src/components/InviteHrTeamDialog.js
import React, { useEffect, useRef, useState } from 'react';
import { authApi } from '../axiosInstance';

/* ---- Helper: Cookie lesen (für CSRF) ------------------------------------ */
function readCookie(name) {
  const hit = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.split('=')[1]) : '';
}

export default function InviteHrTeamDialog({ onClose, onSuccess }) {
  const [mounted, setMounted] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('hr_user'); // 'hr_admin' | 'hr_user'
  const [loading, setLoading] = useState(false);

  // ✅ NEU: Projekte (surveys) laden + Auswahl
  const [surveys, setSurveys] = useState([]); // [{ id: number }]
  const [selectedSurveyIds, setSelectedSurveyIds] = useState([]); // number[]
  const [surveysLoading, setSurveysLoading] = useState(false);

  const [error, setError] = useState('');
  const emailRef = useRef(null);

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());

  useEffect(() => {
    const t = setTimeout(() => {
      setMounted(true);
      emailRef.current?.focus();
    }, 10);

    // CSRF-Cookie defensiv initialisieren (falls noch nicht vorhanden)
    fetch('/auth/csrf-token', { credentials: 'include' }).catch(() => {});

    const onEsc = (e) => e.key === 'Escape' && !loading && onClose?.();
    window.addEventListener('keydown', onEsc);

    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onEsc);
    };
  }, [loading, onClose]);

  // ✅ NEU: Surveys/Projekte laden (kommt als { surveys: [{survey_id:..}] })
  useEffect(() => {
    let alive = true;

    async function loadSurveys() {
      try {
        setSurveysLoading(true);
        setError('');

        const csrf = readCookie('XSRF-TOKEN') || readCookie('_csrf') || '';

        const res = await authApi.get('/api/hr-team/surveys', {
          withCredentials: true,
          headers: csrf ? { 'X-CSRF-Token': csrf } : undefined,
        });

        const raw = res?.data?.surveys;
        const ids = Array.isArray(raw)
          ? raw
              .map((x) => Number(x?.survey_id))
              .filter((n) => Number.isInteger(n) && n > 0)
          : [];

        const unique = [...new Set(ids)].sort((a, b) => b - a);

        if (!alive) return;

        setSurveys(unique.map((id) => ({ id })));

        // UX: wenn genau 1 Projekt existiert → automatisch vorauswählen
        if (unique.length === 1) {
          setSelectedSurveyIds([unique[0]]);
        }
      } catch (err) {
        // wenn das Laden scheitert, zeigen wir nur Hinweis (Invite kann trotzdem hr_admin ohne Projekte,
        // aber hr_user braucht mind. 1 – darum ist die Liste wichtig)
        if (!alive) return;

        const status = err?.response?.status;
        if (status === 401) {
          setError('Nicht angemeldet. Bitte neu einloggen.');
        } else if (status === 403) {
          setError('Keine Berechtigung, Projekte zu laden (HR/HR-Admin erforderlich).');
        } else {
          setError('Projekte konnten nicht geladen werden.');
        }
      } finally {
        if (alive) setSurveysLoading(false);
      }
    }

    loadSurveys();
    return () => {
      alive = false;
    };
  }, []);

  function toggleSurvey(id) {
    setSelectedSurveyIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  }

  async function handleInvite(e) {
    e.preventDefault();
    setError('');

    const cleanEmail = String(email || '').trim();

    if (!isValidEmail(cleanEmail)) {
      setError('Bitte eine gültige E-Mail-Adresse eingeben.');
      return;
    }

    // ✅ NEU: hr_user braucht mind. 1 Projekt
    if (role === 'hr_user' && selectedSurveyIds.length === 0) {
      setError('Bitte mindestens 1 Projekt auswählen.');
      return;
    }

    try {
      setLoading(true);

      // CSRF-Token aus Cookie holen (XSRF-TOKEN bevorzugt, sonst _csrf)
      const csrf = readCookie('XSRF-TOKEN') || readCookie('_csrf') || '';

      await authApi.post(
        '/api/hr-team/invite',
        {
          email: cleanEmail,
          role,
          // ✅ NEU: Projekte mitschicken (Backend akzeptiert survey_ids / surveyIds / projectIds etc.)
          survey_ids: selectedSurveyIds,
        },
        {
          withCredentials: true,
          headers: csrf ? { 'X-CSRF-Token': csrf } : undefined,
        }
      );

      onSuccess?.();
      onClose?.();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error;

      if (status === 401) {
        setError('Nicht angemeldet. Bitte neu einloggen.');
      } else if (status === 403) {
        setError('Keine Berechtigung (HR/HR-Admin erforderlich) oder Sitzung/CSRF fehlt.');
      } else if (status === 400 && typeof msg === 'string' && msg.includes('Mindestens 1 Projekt')) {
        setError('Bitte mindestens 1 Projekt auswählen.');
      } else if (status === 400 && typeof msg === 'string') {
        setError(msg); // z.B. "Unbekannte survey_id(s) ..."
      } else {
        setError(
          msg === 'email_exists'
            ? 'Diese E-Mail ist bereits registriert.'
            : msg === 'duplicate_invitation'
            ? 'Für diese E-Mail existiert bereits eine aktive Einladung.'
            : 'Einladung konnte nicht gesendet werden.'
        );
      }
    } finally {
      setLoading(false);
    }
  }

  const roles = [
    { id: 'hr_admin', title: 'HR-Admin', note: 'volle Rechte' },
    { id: 'hr_user', title: 'HR-User', note: 'Standard' },
  ];

  function onBackdrop(e) {
    if (loading) return;
    if (e.target === e.currentTarget) onClose?.();
  }

  const inviteDisabled =
    loading ||
    !isValidEmail(email) ||
    (role === 'hr_user' && selectedSurveyIds.length === 0) ||
    surveysLoading;

  return (
    <div
      className="fixed inset-0 z-[1100] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={onBackdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-title"
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className={[
          'w-full max-w-lg rounded-3xl',
          'bg-slate-900/98 text-white',
          'ring-1 ring-white/10 shadow-2xl shadow-black/40',
          'transition-all duration-200 ease-out',
          mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-95',
        ].join(' ')}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-3">
          <h2 id="invite-title" className="text-[20px] md:text-[22px] font-semibold tracking-tight">
            HR-Teammitglied einladen
          </h2>
          <p className="mt-1 text-sm text-gray-300">
            Sende eine Einladung per E-Mail und wähle die passende HR-Rolle.
          </p>
        </div>

        <div className="h-px w-full bg-white/10" />

        {/* Body */}
        <form onSubmit={handleInvite} className="px-6 py-5 space-y-5">
          {error && (
            <div className="rounded-2xl border border-red-500/40 bg-red-900/30 px-4 py-3 text-red-100">
              {error}
            </div>
          )}

          {/* Email */}
          <div>
            <label className="mb-2 block text-sm text-gray-200">
              E-Mail <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <input
                ref={emailRef}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-2xl bg-slate-800 text-white placeholder-gray-400
                           border border-white/15 focus:outline-none focus:ring-2 focus:ring-mentixxgreen/80
                           focus:border-transparent px-3.5 py-3 text-[15px]"
                placeholder="name@firma.de"
                autoComplete="off"
                required
              />
            </div>
          </div>

          {/* Role segmented control */}
          <div>
            <label className="mb-2 block text-sm text-gray-200">Rolle</label>
            <div className="grid grid-cols-2 gap-2">
              {roles.map((r) => {
                const active = role === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setRole(r.id)}
                    aria-pressed={active}
                    className={[
                      'rounded-2xl border px-4 py-3 text-left transition',
                      active
                        ? 'bg-mentixxgreen text-black border-mentixxgreen shadow-lg shadow-mentixxgreen/25'
                        : 'bg-slate-800 text-gray-200 border-white/15 hover:bg-slate-700',
                    ].join(' ')}
                    style={active ? { backgroundColor: '#98FB98' } : undefined}
                  >
                    <div className="font-semibold leading-5">{r.title}</div>
                    <div className={active ? 'text-black/70 text-xs' : 'text-gray-400 text-xs'}>
                      {r.note}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ✅ NEU: Projekt-Auswahl */}
          <div>
            <div className="flex items-baseline justify-between">
              <label className="block text-sm text-gray-200">
                Projekte {role === 'hr_user' ? <span className="text-red-400">*</span> : null}
              </label>
              <div className="text-xs text-gray-400">
                {surveysLoading ? 'lade…' : `${selectedSurveyIds.length} ausgewählt`}
              </div>
            </div>

            <p className="mt-1 text-xs text-gray-400">
              {role === 'hr_user'
                ? 'HR-User braucht mindestens 1 Projekt.'
                : 'Für HR-Admin optional (darf 0 Projekte haben).'}
            </p>

            <div className="mt-3 space-y-2">
              {!surveysLoading && surveys.length === 0 && (
                <div className="rounded-2xl border border-white/10 bg-slate-800/70 px-4 py-3 text-sm text-gray-200">
                  Keine Projekte gefunden.
                </div>
              )}

              {surveys.map((s) => {
                const checked = selectedSurveyIds.includes(s.id);
                return (
                  <label
                    key={s.id}
                    className={[
                      'flex items-center justify-between gap-3',
                      'rounded-2xl border px-4 py-3',
                      checked
                        ? 'border-mentixxgreen bg-mentixxgreen/15'
                        : 'border-white/15 bg-slate-800 hover:bg-slate-700',
                      surveysLoading ? 'opacity-60' : '',
                    ].join(' ')}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[#98FB98]"
                        checked={checked}
                        onChange={() => toggleSurvey(s.id)}
                        disabled={surveysLoading}
                      />
                      <div className="text-sm text-gray-100">
                        Projekt #{s.id}
                        <div className="text-xs text-gray-400">survey_id: {s.id}</div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">{checked ? 'aktiv' : '—'}</div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => !loading && onClose?.()}
              className="rounded-2xl bg-slate-800 text-white px-4 py-2.5 font-medium
                         hover:bg-slate-700 border border-white/10 focus:outline-none focus:ring-2 focus:ring-white/30"
              disabled={loading}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={inviteDisabled}
              className={`rounded-2xl px-4 py-2.5 font-semibold text-black focus:outline-none focus:ring-2
                         ${
                           inviteDisabled
                             ? 'bg-mentixxgreen/60 cursor-not-allowed'
                             : 'bg-mentixxgreen hover:bg-green-400 shadow-md shadow-mentixxgreen/20'
                         }`}
              style={{ backgroundColor: inviteDisabled ? '#98FB98' : undefined }}
            >
              {loading ? 'Sende…' : 'Einladung senden'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
